import { CSSInterpolation } from '@emotion/serialize';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { Breakpoint as MuiBreakpoint } from '@material-ui/core/styles/createBreakpoints';
import { OmakaseBoxShadowsStringType, omakaseBoxShadows } from 'theme/themeCssVariables';
import {
  OmakaseColorsLegacyDecorative,
  ScopedOverride,
  muiPaletteColors,
  omakaseLegacyDecorativeColors,
  scopedOverride,
} from 'theme/colors/omakaseColors';
import {
  OmakaseTypographyVariant,
  getTypographyVariant,
  sansCondensedFamily,
  sansRegularFamily,
} from 'theme/typography/omakaseTypography';
import { Property } from 'csstype';
import { SerializedStyles, css } from '@emotion/react';
import { ThemeVarsObject, omakase } from '@tock/tokens';
import { createTheme } from '@material-ui/core/styles';
import aspectRatio from 'theme/utils/aspectRatio';

export type Breakpoint = MuiBreakpoint;

const hover = (styles: CSSProperties) => {
  return {
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': styles,
    },
  };
};

const noHover = (styles: CSSProperties) => {
  return {
    '@media (hover: none)': styles,
  };
};

/**
 *
 * @param image the background image, non webp, that also exists in the same location as the webp verson
 */
const webPBackgroundImage = (image: string) => {
  const webp = image.substring(0, image.lastIndexOf('.')) + '.webp';
  return {
    backgroundImage: `url(${webp})`,
    '.webp &': {
      backgroundImage: `url(${webp})`,
    },
    '.no-webp &': {
      backgroundImage: `url(${image})`,
    },
  };
};

const webPBackgroundImageEmotion = (image: string) => {
  const webp = image.substring(0, image.lastIndexOf('.')) + '.webp';
  return css`
    background-image: url(${webp});
    .webp & {
      background-image: url(${webp});
    }
    .no-webp & {
      background-image: url(${image});
    }
  `;
};

// Another potential approach to adding custom props to the theme.
// https://www.bergqvist.it/blog/2020/6/26/extending-theme-material-ui-with-typescript
declare module '@material-ui/core/styles/createTypography' {
  interface TypographyOptions {
    fontFamilyCondensed: string;
    /**
     * @deprecated - consider using fontWeightBook
     */
    fontWeightRegular: React.CSSProperties['fontWeight'];
    fontWeightBook: React.CSSProperties['fontWeight'];
    /**
     * @deprecated - avoid using if possible - to be replaced by more thorough Typography system
     */
    temporaryFontSizes: {
      size20: string;
      size18: string;
      size16: string;
      size14: string;
      size12: string;
    };

    variant: (variant: OmakaseTypographyVariant) => SerializedStyles;
  }
  interface Typography {
    fontFamilyCondensed: string;
    /**
     * @deprecated - consider using fontWeightBook
     */
    fontWeightRegular: React.CSSProperties['fontWeight'];
    fontWeightBook: React.CSSProperties['fontWeight'];
    /**
     * @deprecated - avoid using if possible - to be replaced by more thorough Typography system
     */
    temporaryFontSizes: {
      size20: string;
      size18: string;
      size16: string;
      size14: string;
      size12: string;
    };

    variant: typeof getTypographyVariant;
  }
}
declare module '@material-ui/core/styles/zIndex' {
  interface ZIndex {
    /**
     * @deprecated
     */
    mobileStepper: number;
    /**
     * @deprecated - consider using zIndex.footer for equivalent z-index value
     */
    speedDial: number;
    /**
     * @deprecated - consider using zIndex.header for equivalent z-index value
     */
    appBar: number;
    /**
     * @deprecated
     */
    snackbar: number;
    /**
     * bump: used to make minor adjustments in stacking order (to be used alone, or in combination with another zIndex key, e.g., zIndex.footer + zIndex.bump)
     */
    bump: number;
    /**
     * footer: used to control stacking order of page footer
     */
    footer: number;
    /**
     * header: used to control stacking order of page header (both desktop and mobile)
     */
    header: number;
    /**
     * drawer: used to control stacking order of sliding drawers, outside of the application's navigation drawer (handled by zIndex.navigation)
     */
    drawer: number;
    /**
     * popover: used to control stacking order of popover or overlay containers (e.g., date selector)
     */
    popover: number;
    /**
     * navigation: used to control stacking order of application's navigation menus
     */
    navigation: number;
    /**
     * modal: used to control stacking order of modal containers or modal-like objects
     */
    modal: number;
    /**
     * modal: used to control stacking order of tooltips
     */
    tooltip: number;
  }
}

declare module '@material-ui/core/styles/createBreakpoints' {
  interface BreakpointOverrides {
    lgx: true;
  }
}

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    layout: {
      hideScrollbar: CSSProperties;
      hideScrollbarAsString: string;
    };
    images: {
      webPBackgroundImage: (image: string) => CSSProperties;
      webPBackgroundImageEmotion: (image: string) => SerializedStyles;
    };
    pointers: {
      hover: (styles: CSSProperties) => CSSProperties;
      noHover: (styles: CSSProperties) => CSSProperties;
    };
    shadow: {
      100: string | `var(--${string})`;
      200: string | `var(--${string})`;
    };

    /**
     * @deprecated This has been replaced with the custom key 'shadow'.
     */
    boxShadows: OmakaseBoxShadowsStringType & {
      one: string;
      two: string;
      focus: string;
    };
    mediaRatios: {
      sixteenNine: string;
      twentyOneNine: string;
      oneOne: string;
      threeTwo: string;
      fourThree: string;
      oneEightFiveOne: string;
      aspectRatio: (width: number, height: number) => string;
    };
    colors: ThemeVarsObject['color'];
    borderRadius: {
      /*
       * The smaller border radius
       */
      100: string | `var(--${string})`;
      /*
       * The larger border radius
       */
      200: string | `var(--${string})`;
      /*
       * This borderRadius provides completely rounded edges like a pill shape
       */
      Pill: '999em' | `var(--${string})`;
    };
    legacyColors: OmakaseColorsLegacyDecorative;
    interactiveStates: {
      active: (val: SerializedStyles) => SerializedStyles;
      checked: (val: SerializedStyles) => SerializedStyles;
      disabled: (val: SerializedStyles) => SerializedStyles;
      focus: (val: SerializedStyles) => SerializedStyles;
      hover: (val: SerializedStyles) => SerializedStyles;
    };
    utils: {
      aspectRatio: (
        width: number,
        height: number,
        objectFit?: Property.ObjectFit
      ) => SerializedStyles;
    };
    timings: {
      duration: {
        shortest: string;
        shorter: string;
        short: string;
        standard: string;
        complex: string;
        slow: string;
        enteringScreen: string;
        leavingScreen: string;
      };
      timingFunction: {
        easeInOut: string;
        easeOut: string;
        easeIn: string;
        sharp: string;
      };
    };
    scopedOverride: ScopedOverride;
  }
  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    layout: {
      hideScrollbar: CSSProperties;
      hideScrollbarAsString: string;
    };
    images: {
      webPBackgroundImage: (image: string) => CSSProperties;
      webPBackgroundImageEmotion: (image: string) => SerializedStyles;
    };
    pointers: {
      hover: (styles: CSSProperties) => CSSProperties;
      noHover: (styles: CSSProperties) => CSSProperties;
    };
    mediaRatios: {
      sixteenNine: string;
      twentyOneNine: string;
      oneOne: string;
      threeTwo: string;
      fourThree: string;
      oneEightFiveOne: string;
      aspectRatio: (width: number, height: number) => string;
    };
    colors: ThemeVarsObject['color'];
    borderRadius: {
      /*
       * The smaller border radius
       */
      100: string | `var(--${string})`;
      /*
       * The larger border radius
       */
      200: string | `var(--${string})`;
      /*
       * This borderRadius provides completely rounded edges like a pill shape
       */
      Pill: '999em' | `var(--${string})`;
    };
    legacyColors: OmakaseColorsLegacyDecorative;
    shadow: {
      100: string | `var(--${string})`;
      200: string | `var(--${string})`;
    };

    /**
     * @deprecated This has been replaced with the custom key 'shadow'.
     */
    boxShadows: OmakaseBoxShadowsStringType & {
      one: string;
      two: string;
      focus: string;
    };
    interactiveStates: {
      active: (val: SerializedStyles) => SerializedStyles;
      checked: (val: SerializedStyles) => SerializedStyles;
      disabled: (val: SerializedStyles) => SerializedStyles;
      focus: (val: SerializedStyles) => SerializedStyles;
      hover: (val: SerializedStyles) => SerializedStyles;
    };
    utils: {
      aspectRatio: (
        width: number,
        height: number,
        objectFit?: Property.ObjectFit
      ) => SerializedStyles;
    };
    timings: {
      duration: {
        shortest: string;
        shorter: string;
        short: string;
        standard: string;
        complex: string;
        slow: string;
        enteringScreen: string;
        leavingScreen: string;
      };
      timingFunction: {
        easeInOut: string;
        easeOut: string;
        easeIn: string;
        sharp: string;
      };
    };
    scopedOverride: ScopedOverride;
  }
}

export const noShadow = {
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
  '&:active': {
    boxShadow: 'none',
  },
  '&:focus': {
    boxShadow: 'none',
  },
  '@media (hover: none)': {
    boxShadow: 'none',
  },
};

export const getTheme = (
  type: 'light' = 'light',
  syndicationOverrides?: { backgroundColor?: string; primaryColor?: string; font?: string }
) => {
  const colors = omakase.color;
  const legacyColors: OmakaseColorsLegacyDecorative = {
    ...omakaseLegacyDecorativeColors,
  };

  const colorPrimary = syndicationOverrides?.primaryColor ?? muiPaletteColors.MuiPalettePrimary;

  return createTheme({
    palette: {
      type,
      action: {
        hover: muiPaletteColors.MuiPaletteHover,
        // disabled: colors.disabled,
        // disabledBackground: colors.disabledBackground,
      },
      background: {
        default: syndicationOverrides?.backgroundColor ?? 'transparent',
      },
      primary: {
        main: colorPrimary,
      },
      secondary: {
        main: muiPaletteColors.MuiPaletteSecondary,
      },
      error: {
        main: muiPaletteColors.MuiPaletteError,
      },
      success: {
        main: muiPaletteColors.MuiPaletteSuccess,
      },
      info: {
        main: muiPaletteColors.MuiPaletteHover,
      },
      text: {
        primary: muiPaletteColors.MuiPaletteText,
      },
    },
    breakpoints: {
      keys: ['xs', 'sm', 'md', 'lg', 'lgx', 'xl'],
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        lgx: 1440,
        xl: 1920,
      },
    },
    layout: {
      hideScrollbar: {
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none', // Firefox
        '&::-webkit-scrollbar': {
          display: 'none', // Safari + Chrome
        },
      },
      // This is cribbed from dashboard/src/js/theme/Theme.tsx , it is easier to use from within emotion.
      hideScrollbarAsString: `
        ::-webkit-scrollbar {
          display: none;
        }
        -ms-overflow-style: none;
        scrollbar-width: none;
      `,
    },
    images: {
      webPBackgroundImage: webPBackgroundImage,
      webPBackgroundImageEmotion: webPBackgroundImageEmotion,
    },
    pointers: {
      hover: hover,
      noHover: noHover,
    },
    shadow: {
      100: omakase.shadow[100],
      200: omakase.shadow[200],
    },
    boxShadows: {
      one: '1px 1px 3px rgba(0, 0, 0, 0.05)',
      two: '0px 4px 8px rgba(0, 0, 0, 0.1)',
      focus: '0 0 1px 3px rgba(59, 153, 252, 0.7)',
      ...omakaseBoxShadows,
    },
    mediaRatios: {
      sixteenNine: '56.25%', // 16:9
      twentyOneNine: '42.85%',
      oneOne: '100%', // 1:1,
      oneEightFiveOne: '54.05%', // 1.185/1 cinema film
      threeTwo: '66.66%', // 3:2
      fourThree: '75%', // 4:3,
      aspectRatio: (width: number, height: number) => `${(height / width) * 100}%`,
    },
    colors,
    legacyColors,
    typography: {
      fontFamily: syndicationOverrides?.font ?? sansRegularFamily,
      fontFamilyCondensed: sansCondensedFamily,
      htmlFontSize: 10,
      fontWeightBold: 700,
      fontWeightMedium: 500,
      fontWeightRegular: 400,
      fontWeightBook: 400,
      temporaryFontSizes: {
        size20: '2rem',
        size18: '1.8rem',
        size16: '1.6rem',
        size14: '1.4rem',
        size12: '1.2rem',
      },
      variant: getTypographyVariant,
    },
    borderRadius: {
      100: omakase.borderRadius[100],
      200: omakase.borderRadius[200],
      Pill: omakase.borderRadius.Pill,
    },
    interactiveStates: {
      active: (val: CSSInterpolation) => css`
        &:active:not(:disabled):not([aria-disabled='true']) {
          ${val};
        }
      `,
      checked: (val: CSSInterpolation) => css`
        &:checked {
          ${val};
        }
      `,
      disabled: (val: CSSInterpolation) => css`
        &:disabled,
        &[aria-disabled='true'] {
          ${val};
        }
      `,
      focus: (val: CSSInterpolation) => css`
        body.no-focus-visible &:focus {
          ${val};
        }
        &:focus-visible {
          ${val};
        }
      `,
      hover: (val: CSSInterpolation) => css`
        @media (hover: hover) {
          &:hover:not(:disabled):not([aria-disabled='true']):not(:active) {
            ${val};
          }
        }
      `,
    },
    utils: {
      aspectRatio: aspectRatio,
    },
    scopedOverride,
    timings: {
      duration: {
        shortest: '150ms',
        shorter: '200ms',
        short: '250ms',
        standard: '300ms',
        complex: '375ms',
        slow: '800ms',
        enteringScreen: '225ms',
        leavingScreen: '195ms',
      },
      timingFunction: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
    },
    zIndex: {
      bump: 10,
      footer: 1050,
      header: 1100,
      drawer: 1200,
      popover: 1225,
      navigation: 1250,
      modal: 1300,
      tooltip: 1500,
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          '*': {
            // NOTE: This update sets the stylistic set of Oracle to 16 which fixes an issue with the "i" character on low res monitors. This does not have any affect on our other font Sohne but if you run into issue with a font change in the future please see if this is the cause. This needs to be applied at both the * and body level to function everywhere.
            fontFeatureSettings: '"ss16" 1',
          },
          body: {
            // NOTE: This update sets the stylistic set of Oracle to 16 which fixes an issue with the "i" character on low res monitors. This does not have any affect on our other font Sohne but if you run into issue with a font change in the future please see if this is the cause. This needs to be applied at both the * and body level to function everywhere.
            fontFeatureSettings: '"ss16" 1',
          },
        },
      },
      MuiFormHelperText: {
        root: {
          color: colors.fgDefault,
        },
      },
      MuiDivider: {
        root: {
          backgroundColor: colors.borderDefault,
        },
      },
      MuiButton: {
        root: {
          ...noShadow,
        },
        contained: {
          ...noShadow,
        },
        containedPrimary: {
          ...noShadow,
        },
        containedSecondary: {
          ...noShadow,
        },
        label: {
          textTransform: 'none',
          fontFamily: syndicationOverrides?.font ?? sansRegularFamily,
          fontWeight: 500, // TODO: do this with theme var
        },
        containedSizeSmall: {
          padding: '5px 10px',
          ...noShadow,
        },
        containedSizeLarge: {
          padding: '11px 22px',
          ...noShadow,
        },
        outlinedSizeSmall: {
          padding: '4px 9px',
          ...noShadow,
        },
        outlinedSizeLarge: {
          padding: '10px 21px',
          ...noShadow,
        },
        textSizeSmall: {
          padding: '5px 10px',
          ...noShadow,
        },
        textSizeLarge: {
          padding: '11px 22px',
          ...noShadow,
        },
      },
      MuiSvgIcon: {
        root: {
          fontSize: '2.4rem',
        },

        fontSizeSmall: {
          fontSize: '2.0rem',
        },

        fontSizeLarge: {
          fontSize: '2.8rem',
        },
      },
    },
  });
};
